/**
 * A summary of a failure that is safe to put in a browser console.
 *
 * Logging an axios error object directly also logs `error.config.data`, the
 * serialised request body — and for `/api/auth/token` that body is the Azure ID
 * token, a bearer credential. DevTools shows it as soon as anyone expands the
 * logged object, and a failed sign-in logs the same error more than once. This
 * returns only the fields worth having in a support conversation.
 */
interface ErrorSummary {
  message: string;
  code?: string;
  status?: number;
  serverMessage?: string;
}

interface ErrorLike {
  message?: unknown;
  code?: unknown;
  response?: { status?: unknown; data?: { error?: { message?: unknown }; message?: unknown } };
}

export function describeError(error: unknown): ErrorSummary {
  if (typeof error === "string") return { message: error };
  if (error === null || typeof error !== "object") return { message: String(error) };

  const err = error as ErrorLike;
  const summary: ErrorSummary = {
    message: typeof err.message === "string" ? err.message : String(error),
  };

  if (typeof err.code === "string") summary.code = err.code;
  if (typeof err.response?.status === "number") summary.status = err.response.status;

  const body = err.response?.data;
  const serverMessage = body?.error?.message ?? body?.message;
  if (typeof serverMessage === "string") summary.serverMessage = serverMessage;

  return summary;
}
